import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ErrorState } from "@/components/brand/error-state";
import { PageHeader } from "@/components/layout/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CONNECT_ERRORS,
  ConnectionCard,
  PageReportView,
  useFirstFetch,
  usePageStatus,
} from "@/features/social-page";

/**
 * The company page, for the SMM.
 *
 * One screen for the whole relationship with the page: connect it, see that the
 * connection is healthy, and read how the page is doing. The publishing itself
 * lives on the monthly plan, where the posts are.
 *
 * This is the one analytics surface in the product that is about the brand
 * rather than about people, so it carries no privacy caveat: everything here is
 * the organisation's own page and the SMM's own posts.
 */
type Search = { connected?: string; error?: string };

export const Route = createFileRoute("/_authenticated/org/page")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    connected: typeof search.connected === "string" ? search.connected : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: CompanyPagePage,
});

function CompanyPagePage() {
  // Which page's report is on screen. Null means the default one, which is
  // what the API picks when no page is named.
  const [viewing, setViewing] = useState<string | null>(null);
  const status = usePageStatus();
  // A page chosen for the first time is read straight away.
  useFirstFetch(status.data);
  const { connected, error } = Route.useSearch();
  const navigate = useNavigate();

  // The callback hands its result back in the query string, because a browser
  // mid-redirect cannot carry anything else. Say it once, then clear it so a
  // refresh does not repeat the message.
  useEffect(() => {
    if (!connected && !error) return;
    if (connected) toast.success("Your page is connected");
    if (error) toast.error(CONNECT_ERRORS[error] ?? "That connection did not complete.");
    void navigate({ to: "/org/page", search: {}, replace: true });
  }, [connected, error, navigate]);

  return (
    <div>
      <PageHeader
        eyebrow="Your team"
        title="Company page"
        description="Connect your pages, publish from the monthly plan, and see how each one is doing."
      />

      <div className="space-y-6">
        {status.isPending ? (
          <Skeleton className="h-32 rounded-lg" />
        ) : status.isError ? (
          <ErrorState onRetry={() => void status.refetch()} />
        ) : (
          <ConnectionCard status={status.data} viewing={viewing} onView={setViewing} />
        )}

        <PageReportView viewing={viewing} />
      </div>
    </div>
  );
}
